/**
 * useAdvisoryRequests Hook
 *
 * Hook for orchestrator dashboard to manage advisory requests
 * Extends base useLeadRequests with orchestrator-specific functionality
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useToast } from 'hooks/useToast';
import { advisoryService } from 'services/advisoryService';
import { LeadRequestWithUserData, LeadRequestStatus } from 'types/leadRequestsTypes';

export type SortOption = 'date-newest' | 'date-oldest' | 'status' | 'requester';
export type ViewMode = 'grid' | 'list';

interface UseAdvisoryRequestsReturn {
  requests: LeadRequestWithUserData[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  size: number;
  setPage: (page: number) => void;
  setSize: (size: number) => void;
  fetchRequests: () => Promise<void>;
  updateStatus: (id: string, status: LeadRequestStatus) => Promise<void>;
  fetchRequestDetails: (id: string) => Promise<LeadRequestWithUserData>;
  refetch: () => Promise<void>;
  statusCounts: Record<LeadRequestStatus, number>;
  sortBy: SortOption;
  setSortBy: (sort: SortOption) => void;
  viewMode: ViewMode;
  setViewMode: (view: ViewMode) => void;
  isLoadingMore: boolean;
}

interface Filters {
  searchTerm?: string;
  status?: LeadRequestStatus | string;
}

export const useAdvisoryRequests = (filters: Filters = {}): UseAdvisoryRequestsReturn => {
  const [allRequests, setAllRequests] = useState<LeadRequestWithUserData[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(12);
  const [sortBy, setSortBy] = useState<SortOption>('date-newest');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [statusCounts, setStatusCounts] = useState<Record<LeadRequestStatus, number>>({
    [LeadRequestStatus.NEW]: 0,
    [LeadRequestStatus.REQUESTED]: 0,
    [LeadRequestStatus.ACCEPTED]: 0,
    [LeadRequestStatus.REJECTED]: 0,
    [LeadRequestStatus.CANCELED]: 0,
  });

  const { toastError, toastSuccess } = useToast();

  // Sort requests based on selected option (now can sort by actual user names!)
  const sortRequests = useCallback(
    (requests: LeadRequestWithUserData[], sortOption: SortOption): LeadRequestWithUserData[] => {
      const sorted = [...requests];

      switch (sortOption) {
        case 'date-newest':
          return sorted.sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        case 'date-oldest':
          return sorted.sort(
            (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
        case 'status':
          return sorted.sort((a, b) => a.status.localeCompare(b.status));
        case 'requester':
          // Now we can sort by actual user names instead of IDs!
          return sorted.sort((a, b) => {
            const nameA = a.requester_user_data
              ? `${a.requester_user_data.first_name} ${a.requester_user_data.last_name}`
              : '';
            const nameB = b.requester_user_data
              ? `${b.requester_user_data.first_name} ${b.requester_user_data.last_name}`
              : '';
            return nameA.localeCompare(nameB);
          });
        default:
          return sorted;
      }
    },
    []
  );

  // Enhanced client-side filtering with user data (can now search by names!)
  const filteredAndSortedRequests = useMemo(() => {
    let filtered = [...allRequests];

    // Client-side filtering
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(req => {
        // Search in ID
        if (req.id.toLowerCase().includes(searchLower)) return true;

        // Search in requester name
        if (req.requester_user_data) {
          const fullName =
            `${req.requester_user_data.first_name} ${req.requester_user_data.last_name}`.toLowerCase();
          if (fullName.includes(searchLower)) return true;

          // Search in email
          if (req.requester_user_data.email?.toLowerCase().includes(searchLower)) return true;
        }

        return false;
      });
    }

    if (filters.status) {
      filtered = filtered.filter(req => req.status === filters.status);
    }

    // Apply sorting
    return sortRequests(filtered, sortBy);
  }, [allRequests, filters.searchTerm, filters.status, sortBy, sortRequests]);

  // Paginate requests
  const paginatedRequests = useMemo(() => {
    const startIndex = (page - 1) * size;
    const endIndex = startIndex + size;
    return filteredAndSortedRequests.slice(startIndex, endIndex);
  }, [filteredAndSortedRequests, page, size]);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await advisoryService.getLeadRequests({
        page: 1,
        size: 100, // Fetch all for client-side filtering/sorting
      });

      const fetchedRequests = response.items || [];
      setAllRequests(fetchedRequests);
      setTotal(fetchedRequests.length);

      // Calculate status counts
      const counts: Record<LeadRequestStatus, number> = {
        [LeadRequestStatus.NEW]: 0,
        [LeadRequestStatus.REQUESTED]: 0,
        [LeadRequestStatus.ACCEPTED]: 0,
        [LeadRequestStatus.REJECTED]: 0,
        [LeadRequestStatus.CANCELED]: 0,
      };

      fetchedRequests.forEach(req => {
        if (req.status in counts) {
          counts[req.status as LeadRequestStatus]++;
        }
      });

      setStatusCounts(counts);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Falha ao carregar solicitações';
      setError(errorMessage);
      toastError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [toastError]);

  const fetchRequestDetails = useCallback(
    async (id: string) => {
      try {
        const response = await advisoryService.getLeadRequest(id);
        return response;
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message || 'Falha ao carregar detalhes da solicitação';
        toastError(errorMessage);
        throw err;
      }
    },
    [toastError]
  );

  const updateStatus = useCallback(
    async (id: string, status: LeadRequestStatus) => {
      try {
        await advisoryService.updateLeadRequest(id, { status });
        toastSuccess('Status da solicitação atualizado com sucesso');
        await fetchRequests();
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message || 'Falha ao atualizar status da solicitação';
        toastError(errorMessage);
        throw err;
      }
    },
    [fetchRequests, toastError, toastSuccess]
  );

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  return {
    requests: paginatedRequests,
    total: filteredAndSortedRequests.length,
    loading,
    error,
    page,
    size,
    setPage,
    setSize,
    fetchRequests,
    fetchRequestDetails,
    updateStatus,
    refetch: fetchRequests,
    statusCounts,
    sortBy,
    setSortBy,
    viewMode,
    setViewMode,
    isLoadingMore,
  };
};
